import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Utensils, Users, Music, Car, MapPin, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import TeamSelectionModal from '../components/TeamSelectionModal';
import { generateTailgateGuide, parseTailgateGuide, isOpenAIConfigured } from '../utils/openai';
import { getUpcomingGames } from '../data/schedules';
import { collegeTeams, nflTeams, nbaTeams, nhlTeams, mlbTeams, cbbTeams } from '../data/teams';

const TailgateCentral = () => {
  const { selectedTeam, teamColors, selectTeam } = useTheme?.() || {};
  const teamName = selectedTeam?.name || '';
  const venue = selectedTeam?.stadium || selectedTeam?.venue || '';
  const primary = teamColors?.primary || '#0ea5e9';
  const secondary = teamColors?.secondary || '#f97316';
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiGuide, setAiGuide] = useState(null);
  const [aiGameText, setAiGameText] = useState('');
  const [expanded, setExpanded] = useState({ stadium: true, parking: true, zones: true, bars: true, timeline: true, checklist: true, meta: true });
  const toggle = (key) => setExpanded((s) => ({ ...s, [key]: !s[key] }));
  const [selectedGame, setSelectedGame] = useState(null);
  const [teamCache, setTeamCache] = useState({});
  const [gameOptions, setGameOptions] = useState([]);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const sportType = useMemo(() => {
    if (!teamName) return 'college';
    if (nflTeams.some(t => t.name === teamName)) return 'nfl';
    if (collegeTeams.some(t => t.name === teamName)) return 'college';
    if (nbaTeams.some(t => t.name === teamName)) return 'nba';
    if (nhlTeams.some(t => t.name === teamName)) return 'nhl';
    if (mlbTeams.some(t => t.name === teamName)) return 'mlb';
    if (cbbTeams.some(t => t.name === teamName)) return 'cbb';
    return 'college';
  }, [teamName]);
  const checklistRef = useRef(null);
  const handlePrintChecklist = () => {
    try {
      if (!checklistRef.current) return window.print();
      const html = checklistRef.current.innerHTML;
      const w = window.open('', 'PRINT', 'height=700,width=900');
      if (!w) return window.print();
      w.document.write('<html><head><title>Tailgate Checklist</title>');
      w.document.write('<style>body{font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;padding:20px} h1{font-size:20px;margin-bottom:12px} ul{columns:2; -webkit-columns:2; -moz-columns:2} li{margin:6px 0}</style>');
      w.document.write('</head><body>');
      w.document.write('<h1>Tailgate Checklist</h1>');
      w.document.write(html);
      w.document.write('</body></html>');
      w.document.close();
      w.focus();
      w.print();
      w.close();
    } catch {}
  };

  const blueprint = useMemo(() => {
    if (!teamName) return null;
    return {
      teamName,
      venue,
      // Simple, on-site blueprint sections per spec
      stadiumGuide: {
        sections: [
          { title: 'Venue Map & Gates', detail: 'Primary gates, recommended entrances for lower/upper bowl, will-call, and ADA access.' },
          { title: 'Bag Policy', detail: 'Clear bag policy and prohibited items. Check current rules before you go.' },
          { title: 'Concessions', detail: 'Signature items, lines to avoid, and mobile order availability.' }
        ]
      },
      parking: {
        sections: [
          { title: 'Official Lots', detail: 'Closest lots with pricing and open times. Verify availability on game day.' },
          { title: 'Walk Times', detail: 'Typical walk times from north/south/east/west lots to main gates.' },
          { title: 'Rideshare', detail: 'Pickup/dropoff zones and best post-game egress routes.' }
        ]
      },
      nearby: {
        bars: 10,
        copy: 'Handpicked pre/post-game options near the venue with distance and hours.'
      },
      checklist: [
        'Cooler + ice', 'Grill + fuel', 'Chairs + table', 'Plates/utensils/napkins', 'Speaker + playlist', 'Team gear', 'Trash bags', 'Portable phone charger'
      ],
      recipes: {
        rotation: ['Championship Chili', 'Smoked Wings', 'Pulled Pork Sliders', 'Buffalo Chicken Dip']
      },
      affiliatePanel: {
        title: 'No time? Tailgreeter plans it all for you',
        cta: 'Explore on-site experiences',
        href: 'https://tailgreeter.com/'
      }
    };
  }, [teamName, venue]);
  const tailgateGuides = [
    {
      icon: Utensils,
      title: "Food & Recipes",
      description: "Game day recipes and grilling tips for the perfect tailgate spread",
      tips: ["Pre-cook meats for faster grilling", "Pack extra ice", "Bring aluminum foil", "Don't forget condiments"]
    },
    {
      icon: Users,
      title: "Setup & Planning",
      description: "How to organize the ultimate tailgate party experience",
      tips: ["Arrive early for best spots", "Bring folding tables", "Pack cleanup supplies", "Designate responsibilities"]
    },
    {
      icon: Music,
      title: "Entertainment",
      description: "Games, music, and activities to keep everyone engaged",
      tips: ["Portable speaker with playlist", "Cornhole tournament", "Football toss", "Team trivia games"]
    },
    {
      icon: Car,
      title: "Parking & Logistics",
      description: "Tips for parking, location, and getting to the stadium",
      tips: ["Research parking options", "Plan arrival time", "Know stadium policies", "Coordinate meeting spots"]
    }
  ];

  const handleGenerateAiGuide = async (teamOverride, gameOverride) => {
    const teamForAIName = (typeof teamOverride === 'string' && teamOverride.trim()) ? teamOverride.trim() : teamName;
    if (!teamForAIName) return;
    setAiError('');
    setAiLoading(true);
    setAiGuide(null);
    setSecondsLeft(20);
    const timer = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    try {
      let gameForAI = gameOverride || selectedGame;
      if (!gameForAI && aiGameText.trim()) {
        const parts = aiGameText.split('•').map((s) => s.trim());
        const [opponent, date, venueText] = [parts[0] || '', parts[1] || '', parts[2] || ''];
        gameForAI = { opponent, date, venue: venueText };
      }
      if (!gameForAI) {
        setAiError('Please enter a game (Opponent • YYYY-MM-DD • Venue)');
        setAiLoading(false);
        try { clearInterval(timer); } catch {}
        setSecondsLeft(0);
        return;
      }
      const raw = await generateTailgateGuide(teamForAIName, gameForAI);
      const parsed = parseTailgateGuide(raw);
      setAiGuide(parsed);
    } catch (e) {
      setAiError('Could not generate a tailgate guide right now. Please try again later.');
    } finally {
      setAiLoading(false);
      try { clearInterval(timer); } catch {}
      setSecondsLeft(0);
    }
  };

  const handleFindGames = async () => {
    if (!selectedTeam?.name) return;
    const key = selectedTeam.name.toLowerCase();
    const cached = teamCache[key];
    if (cached) {
      setGameOptions(cached);
      return;
    }
    try {
      const data = await getUpcomingGames(selectedTeam.name, 12);
      const base = data?.fullSchedule || data?.schedule || [];
      const list = base.map((g) => ({ opponent: g.opponent, date: g.date, venue: g.venue, kickoff: g.kickoff }));
      setGameOptions(list);
      setTeamCache((prev) => ({ ...prev, [key]: list }));
    } catch {}
  };

  const handleSelectGame = (g) => {
    setSelectedGame(g);
    setAiGameText(`${g.opponent} • ${g.date || ''} • ${g.venue || ''}`);
    handleGenerateAiGuide(teamName, g);
  };

  // Auto-load upcoming schedule for selected team when no search query is present
  useEffect(() => {
    let cancelled = false;
    const loadSchedule = async () => {
      try {
        if (!selectedTeam) return;
        const data = await getUpcomingGames(selectedTeam.name, 6);
        if (cancelled) return;
        const list = (data?.schedule || []).map((g) => ({ opponent: g.opponent, date: g.date, venue: g.venue, kickoff: g.kickoff }));
        setGameOptions(list);
      } catch {}
    };
    loadSchedule();
    return () => { cancelled = true; };
  }, [selectedTeam]);

  return (
    <div className="min-h-screen pt-20"
      style={{
        background: selectedTeam
          ? `linear-gradient(135deg, ${primary} 0%, ${secondary} 50%, ${primary} 100%)`
          : 'linear-gradient(135deg, #0ea5e9 0%, #f97316 50%, #0ea5e9 100%)'
      }}
    >
      {/* Hero Section */}
      <section className="py-16 text-white" style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Tailgate Central
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-orange-100 max-w-3xl mx-auto">
            The ultimate guide to college sports tailgating. From recipes to setup tips, we've got you covered!
          </p>
          <div className="flex items-center justify-center space-x-2 text-orange-200">
            <Clock size={20} />
            <span>20+ years of tailgating experience</span>
          </div>
        </div>
      </section>

      {/* Team selection modal (reuse from Home) */}
      <TeamSelectionModal 
        isOpen={showTeamModal}
        onSelectTeam={(team) => { selectTeam(team); setShowTeamModal(false); }}
        onClose={() => setShowTeamModal(false)}
      />

      {/* Tailgate Guides */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tailgreeter CTA (above AI Assist) */}
          <a
            href={blueprint?.affiliatePanel?.href || 'https://tailgreeter.com/'}
            target="_blank"
            rel="noopener noreferrer"
            className="block card p-6 mb-8 border-2 border-orange-400 bg-gradient-to-r from-orange-50 to-white"
            style={{ boxShadow: '0 6px 18px rgba(249,115,22,0.15)' }}
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="min-w-[240px]">
                <div className="text-xl md:text-2xl font-bold text-gray-900">
                  No time? <span className="underline">Tailgreeter plans it all for you</span>
                </div>
                <div className="text-gray-700">
                  <span className="underline">Explore on-site experiences</span>
                </div>
              </div>
              <div className="flex-shrink-0">
                <span className="inline-block px-4 py-2 rounded-lg bg-orange-600 text-white font-semibold hover:bg-orange-700">
                  Book a Tailgate
                </span>
              </div>
            </div>
          </a>

          {/* AI Tailgate Assist */}
          <div className="card p-6 mb-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">40 Bags Powered Tailgate Assist</h2>
                <p className="text-gray-600">Select a game and generate a tailored tailgate plan for that city and venue.</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => setShowTeamModal(true)}
                  className="px-3 py-2 rounded-xl text-white"
                  style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`, boxShadow: '0 6px 14px rgba(14,165,233,0.25)' }}
                >
                  Change Team
                </button>
                <button
                  onClick={handleFindGames}
                  className="px-3 py-2 rounded-xl text-white"
                  style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`, boxShadow: '0 6px 14px rgba(14,165,233,0.25)' }}
                >
                  Refresh Upcoming Games
                </button>
                {/* <button
                  onClick={() => handleGenerateAiGuide(teamName)}
                  disabled={aiLoading || !selectedGame || !isOpenAIConfigured()}
                  className={`px-4 py-2 rounded-xl text-white ${aiLoading ? 'opacity-60' : ''}`}
                  style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`, boxShadow: '0 6px 14px rgba(14,165,233,0.25)' }}
                >
                  {aiLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></span>
                      {`Generating… ${secondsLeft ? `(${secondsLeft}s)` : ''}`}
                    </span>
                  ) : 'Generate Guide'}
                </button> */}
              </div>
            </div>
            {/* Countdown progress bar */}
            {aiLoading && (
              <div className="mt-3 w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full"
                  style={{
                    width: `${Math.max(0, Math.min(100, ((20 - secondsLeft) / 20) * 100))}%`,
                    background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`,
                    transition: 'width 1s linear'
                  }}
                />
              </div>
            )}
            {gameOptions.length > 0 && (
              <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-4 py-2 text-sm text-gray-600 bg-gray-50">Upcoming Tailgates for your Favorite Team</div>
                <ul className="max-h-64 overflow-auto">
                  {gameOptions.map((g, i) => (
                    <li key={`${g.opponent}-${g.date}-${i}`} className="flex items-center justify-between px-4 py-2 hover:bg-gray-50">
                      <div className="text-sm text-gray-800">
                        <div className="font-medium">{teamName} vs {g.opponent}</div>
                        <div className="text-gray-500">{g.venue ? `@ ${g.venue}` : ''} {g.date}{g.kickoff ? ` • ${g.kickoff}` : ''}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        {selectedGame && selectedGame.date === g.date && selectedGame.opponent === g.opponent && (
                          <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700">Selected</span>
                        )}
                        <button
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg text-white"
                          style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}
                          onClick={() => handleSelectGame(g)}
                        >
                          Game's Tailgates
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {aiError && <div className="mt-3 p-3 bg-amber-50 text-amber-800 border border-amber-200 rounded-lg text-sm">{aiError}</div>}
            {aiGuide && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card p-0 overflow-hidden">
                  <button onClick={() => toggle('stadium')} className="w-full flex items-center justify-between px-5 py-4 text-left">
                    <h3 className="text-lg font-semibold text-gray-900">Stadium & Policies</h3>
                    {expanded.stadium ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  {expanded.stadium && (
                    <div className="px-5 pb-5">
                      <ul className="text-gray-700 space-y-2 text-sm">
                        <li><span className="font-medium">Bag Policy:</span> {aiGuide.stadium?.bagPolicy || '—'}</li>
                        <li><span className="font-medium">Gates:</span> {aiGuide.stadium?.gates || '—'}</li>
                        <li><span className="font-medium">Concessions:</span> {aiGuide.stadium?.concessions || '—'}</li>
                      </ul>
                    </div>
                  )}
                </div>
                <div className="card p-0 overflow-hidden">
                  <button onClick={() => toggle('parking')} className="w-full flex items-center justify-between px-5 py-4 text-left">
                    <h3 className="text-lg font-semibold text-gray-900">Parking & Rideshare</h3>
                    {expanded.parking ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  {expanded.parking && (
                    <div className="px-5 pb-5">
                      <ul className="text-gray-700 space-y-2 text-sm">
                        <li><span className="font-medium">Official Lots:</span> {aiGuide.parking?.officialLots || '—'}</li>
                        <li><span className="font-medium">Walk Times:</span> {aiGuide.parking?.walkTimes || '—'}</li>
                        <li><span className="font-medium">Rideshare:</span> {aiGuide.parking?.rideshare || '—'}</li>
                      </ul>
                    </div>
                  )}
                </div>
                <div className="card p-0 overflow-hidden">
                  <button onClick={() => toggle('zones')} className="w-full flex items-center justify-between px-5 py-4 text-left">
                    <h3 className="text-lg font-semibold text-gray-900">Tailgate Zones</h3>
                    {expanded.zones ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  {expanded.zones && (
                    <div className="px-5 pb-5">
                      <ul className="text-gray-700 space-y-1 text-sm">
                        {(aiGuide.tailgateZones || []).map((z, i) => (<li key={i}>• {z}</li>))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="card p-0 overflow-hidden">
                  <button onClick={() => toggle('bars')} className="w-full flex items-center justify-between px-5 py-4 text-left">
                    <h3 className="text-lg font-semibold text-gray-900">Bars & Food</h3>
                    {expanded.bars ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  {expanded.bars && (
                    <div className="px-5 pb-5">
                      <ul className="text-gray-700 space-y-2 text-sm">
                        {(aiGuide.barsAndFood || []).map((b, i) => (
                          <li key={i}>
                            <div className="font-medium">{b.name}</div>
                            <div className="text-gray-600">{b.address}{b.hours ? ` • ${b.hours}` : ''}</div>
                            {b.notes && <div className="text-gray-500">{b.notes}</div>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="card p-0 overflow-hidden">
                  <button onClick={() => toggle('timeline')} className="w-full flex items-center justify-between px-5 py-4 text-left">
                    <h3 className="text-lg font-semibold text-gray-900">Timeline</h3>
                    {expanded.timeline ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  {expanded.timeline && (
                    <div className="px-5 pb-5">
                      <ul className="text-gray-700 space-y-1 text-sm">
                        {(aiGuide.timeline || []).map((t, i) => (<li key={i}>{t.time} — {t.activity}</li>))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="card p-0 overflow-hidden">
                  <button onClick={() => toggle('checklist')} className="w-full flex items-center justify-between px-5 py-4 text-left">
                    <h3 className="text-lg font-semibold text-gray-900">Checklist & Tips</h3>
                    {expanded.checklist ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  {expanded.checklist && (
                    <div className="px-5 pb-5">
                      <ul className="text-gray-700 space-y-1 text-sm mb-3">
                        {(aiGuide.checklist || []).map((c, i) => (<li key={i}>✅ {c}</li>))}
                      </ul>
                      <ul className="text-gray-700 space-y-1 text-sm">
                        {(aiGuide.tips || []).map((t, i) => (<li key={i}>• {t}</li>))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="md:col-span-2 text-sm text-gray-500 card p-0 overflow-hidden">
                  <button onClick={() => toggle('meta')} className="w-full flex items-center justify-between px-5 py-4 text-left">
                    <h3 className="text-sm font-semibold text-gray-900">Metadata</h3>
                    {expanded.meta ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  {expanded.meta && (
                    <div className="px-5 pb-5">
                      <div className="flex items-center justify-between">
                        <div>
                          {aiGuide.citations && aiGuide.citations.length > 0 && (
                            <span>Sources: {aiGuide.citations.slice(0, 3).map((c, i) => (<a key={i} className="underline text-blue-600" href={c} target="_blank" rel="noreferrer noopener">{new URL(c).hostname}</a>)).reduce((prev, curr) => [prev, ', ', curr])}</span>
                          )}
                        </div>
                        <div>Last updated: {aiGuide.lastUpdated || '—'}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Complete Tailgating Guide - HIDDEN */}
          {false && (
            <>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Complete Tailgating Guide
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Everything you need to know for an epic gameday experience
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {tailgateGuides.map((guide, index) => {
                  const IconComponent = guide.icon;
                  return (
                    <div key={index} className="card p-8">
                      <div className="flex items-center space-x-4 mb-6">
                        <div className="w-12 h-12 bg-orange-500 text-white rounded-xl flex items-center justify-center">
                          <IconComponent size={24} />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">{guide.title}</h3>
                      </div>
                      <p className="text-gray-600 mb-6 leading-relaxed">{guide.description}</p>
                      <ul className="space-y-2">
                        {guide.tips.map((tip, tipIndex) => (
                          <li key={tipIndex} className="flex items-start space-x-2">
                            <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700">{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Featured Recipe - HIDDEN */}
      {false && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="card p-8 bg-white">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-2 text-gray-900">Featured Tailgate Recipe</h2>
                <h3 className="text-2xl text-orange-600 font-semibold">Championship Chili</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-xl font-semibold mb-4 text-gray-900">Ingredients:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 2 lbs ground beef</li>
                    <li>• 1 large onion, diced</li>
                    <li>• 2 cans kidney beans</li>
                    <li>• 1 can tomato sauce</li>
                    <li>• Chili powder, cumin, salt</li>
                    <li>• Secret ingredient: dark chocolate</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-4 text-gray-900">Instructions:</h4>
                  <ol className="space-y-2 text-gray-700">
                    <li>1. Brown the ground beef with onions</li>
                    <li>2. Add tomato sauce and seasonings</li>
                    <li>3. Simmer for 2 hours minimum</li>
                    <li>4. Add beans in final 30 minutes</li>
                    <li>5. Stir in chocolate before serving</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Arrive in Style */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Arrive in Style</h2>
            <p className="text-gray-600">RV, party bus, limo, or private jet – pick your ride.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a href="https://www.anrdoezrs.net/click-101149850-15734916" target="_blank" rel="noopener noreferrer" className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">RV Share</h3>
              <p className="text-gray-600">Rent RVs near your stadium.</p>
            </a>
            <a href="https://elifelimo.com/" target="_blank" rel="noopener noreferrer" className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Elife Transportation</h3>
              <p className="text-gray-600">Private cars, limos, and shuttles.</p>
            </a>
            <a href="https://www.privatejetfinder.com/?tk=B822CCBBA445" target="_blank" rel="noopener noreferrer" className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Private Jet Finder</h3>
              <p className="text-gray-600">Fly private to the big game.</p>
            </a>
            <a href="https://vrbo.com/affiliate/oWiAOm8" target="_blank" rel="noopener noreferrer" className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Group Houses (VRBO)</h3>
              <p className="text-gray-600">Perfect for squad travel.</p>
            </a>
          </div>
        </div>
      </section>

      {/* Tailgate Partners (Affiliate) */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Tailgate Partners</h2>
            <p className="text-gray-600">Everything you need for game day</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a href="https://ponguniversity.com/" target="_blank" rel="noopener noreferrer" className="card p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-1">Pong University</h3>
              <p className="text-gray-600">Tailgate games and gear</p>
            </a>
            <a href="https://www.yardgames.com/?rfsn=7930666.925f785" target="_blank" rel="noopener noreferrer" className="card p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-1">Yard Games</h3>
              <p className="text-gray-600">Cornhole, ladder toss and more</p>
            </a>
            <a href="https://www.allamericantailgate.com/?afmc=jh001&utm_campaign=jh001&utm_source=leaddyno&utm_medium=affiliate" target="_blank" rel="noopener noreferrer" className="card p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-1">All American Tailgate</h3>
              <p className="text-gray-600">Custom tailgate setups</p>
            </a>
            <a href="https://www.prestophoto.com?aff=301143" target="_blank" rel="noopener noreferrer" className="card p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-1">Presto Photo</h3>
              <p className="text-gray-600">Print your gameday memories</p>
            </a>
            <div className="card p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Books</h3>
              <div className="space-y-2">
                <a href="https://www.amazon.com/dp/B0DBD9PQZH" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline block">Amazon Book 1</a>
                <a href="https://www.amazon.com/dp/B0DBCX6D4G" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline block">Amazon Book 2</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Integration */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Complete Your Tailgate Experience
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Don't forget to book your game tickets and nearby accommodations through our partners
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a 
              href="https://stubhub.prf.hn/l/W4EJzJX" 
              target="_blank" 
              rel="noopener noreferrer"
              className="card p-6 text-center hover:scale-105 transition-transform duration-200"
            >
              <div className="w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center mx-auto mb-4">
                <MapPin size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Game Tickets</h3>
              <p className="text-gray-600">Find the best seats through StubHub</p>
            </a>
            
            <a 
              href="https://expedia.com/affiliate/QloXX5p" 
              target="_blank" 
              rel="noopener noreferrer"
              className="card p-6 text-center hover:scale-105 transition-transform duration-200"
            >
              <div className="w-12 h-12 bg-orange-600 text-white rounded-xl flex items-center justify-center mx-auto mb-4">
                <Car size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Hotels & Travel</h3>
              <p className="text-gray-600">Book accommodations through Expedia</p>
            </a>
            
            <a 
              href="https://vrbo.com/affiliate/oWiAOm8" 
              target="_blank" 
              rel="noopener noreferrer"
              className="card p-6 text-center hover:scale-105 transition-transform duration-200"
            >
              <div className="w-12 h-12 bg-red-600 text-white rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Group Rentals</h3>
              <p className="text-gray-600">Perfect for large tailgate groups</p>
            </a>
          </div>
        </div>
      </section>

      {/* Stadium Guide / Tailgate Blueprint - HIDDEN */}
      {false && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{teamName ? `${teamName} Tailgate Blueprint` : 'Stadium Information'}</h2>
              <p className="text-gray-600">Parking maps, bag policy, concessions, and pre/post-game options.</p>
            </div>

            {blueprint ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <a href={blueprint.affiliatePanel.href} target="_blank" rel="noopener noreferrer" className="card p-6 md:col-span-2 border-2 border-orange-400 bg-gradient-to-r from-orange-50 to-white">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-1">{blueprint.affiliatePanel.title}</h3>
                      <p className="text-gray-700 text-lg">{blueprint.affiliatePanel.cta}</p>
                    </div>
                    <div>
                      <span className="inline-block px-4 py-2 rounded-lg bg-orange-600 text-white font-semibold hover:bg-orange-700">Book a Tailgate</span>
                    </div>
                  </div>
                </a>
                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Stadium Guide{venue ? `: ${venue}` : ''}</h3>
                  <ul className="space-y-2 text-gray-700">
                    {blueprint.stadiumGuide.sections.map((s, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                        <div>
                          <div className="font-medium">{s.title}</div>
                          <div className="text-sm text-gray-600">{s.detail}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Parking & Rideshare</h3>
                  <ul className="space-y-2 text-gray-700">
                    {blueprint.parking.sections.map((s, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                        <div>
                          <div className="font-medium">{s.title}</div>
                          <div className="text-sm text-gray-600">{s.detail}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Nearby Bars & Food</h3>
                  <p className="text-gray-600 mb-3">{blueprint.nearby.copy}</p>
                  <p className="text-sm text-gray-500">Showing top {blueprint.nearby.bars} options (curation in progress)</p>
                </div>

                <div className="card p-6" ref={checklistRef}>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Tailgate Checklist</h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-700">
                    {blueprint.checklist.map((item, i) => (
                      <li key={i} className="flex items-center space-x-2"><span>✅</span><span>{item}</span></li>
                    ))}
                  </ul>
                  <button onClick={handlePrintChecklist} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg">Print Checklist</button>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Gameday Recipes</h3>
                  <ul className="space-y-2 text-gray-700">
                    {blueprint.recipes.rotation.map((name, i) => (
                      <li key={i}>• {name}</li>
                    ))}
                  </ul>
                </div>

                {/* Tailgreeter emphasized at top */}
              </div>
            ) : (
              <div className="card p-6 text-gray-700">Select a team on the Home page to see a tailored tailgate guide.</div>
            )}
          </div>
        </section>
      )}

      {/* How to Tailgate (shortened) - HIDDEN */}
      {false && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">How to Tailgate</h2>
              <p className="text-gray-600">Games, food, drinks, and must‑have gear.</p>
            </div>
            <div className="card p-6 text-gray-700">Keep it simple: arrive early, pack smart, and enjoy the day.</div>
          </div>
        </section>
      )}


      {/* Alumni Links placeholder removed per request */}
    </div>
  );
};

export default TailgateCentral; 